import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { seoArticles } from "@/mocks/seo-articles";
import NotFound from "@/pages/NotFound";

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://vigorbuyspread.us";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) return <NotFound />;

  const articlePageUrl = `${siteUrl}/article/${article.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "author": { "@type": "Organization", "name": "VigorbuySpreadsheet" },
    "datePublished": article.datePublished,
    "dateModified": article.datePublished,
    "mainEntityOfPage": { "@type": "WebPage", "@id": articlePageUrl },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": article.title, "item": articlePageUrl },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors duration-200">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-accent-500 text-xs font-medium px-2 py-0.5 bg-accent-50 rounded-full">{article.category}</span>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
              </nav>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-8">
                <span>Updated {new Date(article.datePublished).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime} read</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span className="text-accent-600 font-medium">{article.category}</span>
              </div>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.imageAlt}
                  className="w-full h-auto"
                />
              </div>

              <div className="prose-custom space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p className="text-lg text-foreground-800 font-medium leading-relaxed">
                  {article.content.intro}
                </p>

                {article.content.sections.map((section) => (
                  <div key={section.heading}>
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                    {section.listItems && (
                      <div className="space-y-4 mt-4">
                        {section.listItems.map((item) => (
                          <div key={item.title} className="flex gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                            <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                              <i className="ri-check-line text-primary-500"></i>
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-foreground-900">{item.title}</p>
                              <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <p className="text-lg text-foreground-800 font-medium leading-relaxed mt-10">
                  {article.content.conclusion}
                </p>

                <div className="mt-12 p-6 md:p-8 bg-accent-50/50 rounded-2xl border border-accent-200/50 text-center">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                    Ready to Transform Your Shopping Experience?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                    Start exploring Vigorbuy Spreadsheet today and discover a smarter way to find products across 14+ curated categories.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="w-full sm:w-auto px-7 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      Explore Vigorbuy Spreadsheet
                    </a>
                    <Link
                      to="/"
                      className="w-full sm:w-auto px-7 py-3 bg-background-50 border border-background-300/60 text-foreground-800 font-medium text-sm rounded-full hover:border-primary-400 hover:text-primary-700 transition-all duration-200 whitespace-nowrap"
                    >
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
}